import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    SparePart,
    SparePartAvailability,
    SparePartGroupList,
    SparePartGroups,
    SpareParts,
    SparePartsCollection,
} from '@ecs-features/result/spare-parts-result/spare-parts.interface';
import { getOptionsWithTranslateHeader } from '@os-components';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    CrossSellingPartData,
    CrossSellingPartResponse,
} from 'src/app/components/dialogs/part-configuration-wizard/components/cross-selling-table/cross-selling-table.interface';
import { PartsRecommendation } from 'src/app/components/parts-recommendation/parts-recommendation.interface';
import { CrossSellingCableResponse } from '../../components/dialogs/part-configuration-wizard/components/cable-configuration/cable-configuration.interface';
import { displayPartByAvailabilityCodeFilter as partByAvailabilityCode } from './spare-part.helper';

export type DriveGroupsWithSparePartsWear = {
    [key: string]: SparePartWithWear[];
};

export type DriveGroupsWithSparePartsRequest = DriveGroupWithSparePartsRequest[];

export type DriveGroupWithSparePartsRequest = {
    driveGroup: string;
    sparePartIds: string[];
};

export type DriveGroupsWithSparePartsWearResponse = DriveGroupWithSparePartsWearResponse[];

export type DriveGroupWithSparePartsWearResponse = {
    driveGroup: string;
    spareParts: SparePartWithWear[];
};

export type SparePartWithWear = {
    id: string;
    wear: boolean;
};

export type PlantsWithSpareParts = PlantWithSpareParts[];
export interface PlantWithSpareParts {
    plantId: string;
    spareParts: { id: string; availabilityCode: string }[];
}

export interface GearOilSparePart extends SparePart {
    uniqueId: string;
    isIndustrialGear: boolean;
}

export interface GearOilOfProduct {
    originalPart: GearOilSparePart;
    driveGroup: DriveGroup;
    replacementParts: SparePart[];
}

export interface DriveGroup {
    title?: string;
    name?: string;
    type: string;
    iconName?: string;
}

export interface SparePartWithAvailability {
    sparePartId: string;
    availability: SparePartAvailability;
}

@Injectable({
    providedIn: 'root',
})
export class SparePartService {
    public constructor(private http: HttpClient) {}

    public getSpareParts(sparePartIds: string[]): Observable<SparePartsCollection> {
        return this.http.post<SparePartsCollection>(
            '/api/spare-parts',
            sparePartIds,
            getOptionsWithTranslateHeader()
        );
    }

    public getSparePartsForSerialNumber(serialNumber: string): Observable<SparePartGroupList> {
        return this.http.get<SparePartGroupList>(
            `/api/product/${serialNumber}/parts`,
            getOptionsWithTranslateHeader()
        );
    }

    public getGearOilPartsForSerialNumber(serialNumber: string): Observable<GearOilOfProduct[]> {
        return this.http.get<GearOilOfProduct[]>(`/api/product/${serialNumber}/gear-oil`);
    }

    public getSparePartsForSparePartList(id: string): Observable<SparePartGroupList> {
        return this.http.get<SparePartGroupList>(
            `/api/spare-part-list/${id}/parts`,
            getOptionsWithTranslateHeader()
        );
    }

    public getSparePartsAvailability(
        plantsWithSpareParts: PlantsWithSpareParts
    ): Observable<SparePartWithAvailability[]> {
        return this.http.post<SparePartWithAvailability[]>('/api/availability', plantsWithSpareParts);
    }

    public getSparePartsWear(
        driveGroupsWithSpareParts: DriveGroupsWithSparePartsRequest
    ): Observable<DriveGroupsWithSparePartsWearResponse> {
        return this.http.post<DriveGroupsWithSparePartsWearResponse>(
            '/api/wear-parts',
            driveGroupsWithSpareParts
        );
    }

    public getSparePartsRecommendations(groups: SparePartGroups): Observable<PartsRecommendation[]> {
        const requestBody = groups.map((group) => ({
            group: group.title,
            driveGroup: group.driveGroup,
            spareParts: group.spareParts?.filter(partByAvailabilityCode).map((part) => ({
                id: part.id,
                itemNumber: part.itemNumber,
                name: part.name,
                installedQuantity: part.installedQuantity,
            })),
        }));

        return this.http.post<PartsRecommendation[]>('/api/product/parts-recommendation', requestBody);
    }

    public getCrossSellingGearOil(spareParts: SpareParts, driveGroups?: DriveGroup[]) {
        return this.http
            .post<CrossSellingPartResponse[]>(
                '/api/parts/gear-oil',
                spareParts.map((part) => part.crossSelling?.gearOil?.specificationId),
                getOptionsWithTranslateHeader()
            )
            .pipe(
                map((oilParts) =>
                    // map selected parts to their respective gear oil position
                    spareParts.reduce((crossSellingPartData, currentPart) => {
                        const oilPart = oilParts.find(
                            (part) => part.sparePartId === currentPart.crossSelling?.gearOil?.specificationId
                        );

                        const driveGroup = driveGroups?.find(
                            (group) => group.title === (currentPart as any).uniqueId.split('_')[0]
                        );

                        return oilPart && currentPart.crossSelling?.gearOil
                            ? [
                                  ...crossSellingPartData,
                                  {
                                      context: {
                                          itemNumber: currentPart.itemNumber,
                                          amount: currentPart.installedQuantity,
                                          name: currentPart.name,
                                          id: currentPart.crossSelling.gearOil.specificationId,
                                          uniqueId: driveGroup
                                              ? `${driveGroup?.title}_${currentPart.crossSelling.gearOil.specificationId}`
                                              : currentPart.crossSelling.gearOil.specificationId,
                                          gearOilQuantity: currentPart.gearOilQuantity,
                                          gearOilUnit: currentPart.unit,
                                          driveGroup: driveGroup
                                              ? ({
                                                    title: driveGroup?.title,
                                                    type: driveGroup?.type,
                                                    iconName: driveGroup?.iconName,
                                                    name: driveGroup?.name,
                                                } as DriveGroup)
                                              : undefined,
                                      },
                                      parts: oilPart.parts.map((sparePart) => ({
                                          isSelectableRow: true,
                                          isDisabled: false,
                                          rowData: sparePart,
                                      })),
                                  },
                              ]
                            : [...crossSellingPartData];
                    }, [] as CrossSellingPartData[])
                )
            );
    }

    public getCrossSellingCableSteps(sparePartIds: string[]) {
        return this.http.post<CrossSellingCableResponse>(
            '/api/parts/cable/steps',
            sparePartIds,
            getOptionsWithTranslateHeader()
        );
    }
}
